import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { useChatContext } from "./providers/ChatProvider";
import { CommandMenu } from "./CommandMenu";
import { MessageList, useIsEmptyChat } from "./MessageList";
import { Composer } from "./Composer";
import { ChatIntro } from "./ChatIntro";
import { ProposalsBanner } from "./ProposalsBanner";
import { MeetingKbBanner } from "./MeetingKbBanner";
import { getProject, type Project } from "../../api/projects";
import { ProjectContextStrip } from "./ProjectContextStrip";
import RunningTasksStrip from "./RunningTasksStrip";
import { GoalProgressStrip } from "./GoalProgressStrip";
import { KeepWebAnswer } from "./KeepWebAnswer/KeepWebAnswer";
import { useGoalState } from "../../chat/hooks/useGoalState";
import { useVoiceSession } from "../../voice/useVoiceSession";
import { VoiceMode } from "../../voice/VoiceMode";
import { TurnStatus } from "../../chat/hooks/useStreamingTurn";

function ProjectContextStripHeader() {
  const { projectId, features } = useChatContext();
  const [project, setProject] = useState<Project | null>(null);

  useEffect(() => {
    if (!features.projects || !projectId) {
      setProject(null);
      return;
    }
    let cancelled = false;
    getProject(projectId)
      .then((p) => { if (!cancelled) setProject(p); })
      .catch(() => { if (!cancelled) setProject(null); });
    return () => { cancelled = true; };
  }, [projectId, features.projects]);

  if (!features.projects) return null;
  return <ProjectContextStrip project={project} />;
}

export interface VoiceComposerProps {
  centered?: boolean;
  /** Runtime voice_service base URL (embed widget). Falls back to the
   *  build-time VITE_VOICE_SERVICE_URL for the SPA. */
  voiceBaseUrl?: string;
  /** Bearer resolver for the token fetch (embed userJWT; SPA uses cookies). */
  getVoiceAuthToken?: () => string | Promise<string>;
}

export function ComposerWithVoice({
  centered = false,
  voiceBaseUrl: voiceBaseUrlProp,
  getVoiceAuthToken,
}: VoiceComposerProps) {
  const voiceBaseUrl =
    voiceBaseUrlProp ??
    (import.meta.env.VITE_VOICE_SERVICE_URL as string | undefined);
  const voiceEnabled = Boolean(voiceBaseUrl);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const session = useVoiceSession({
    baseUrl: voiceBaseUrl ?? "",
    getAuthToken: getVoiceAuthToken,
  });

  const openVoice = () => { setVoiceOpen(true); session.connect(); };
  const closeVoice = () => setVoiceOpen(false);

  return (
    <>
      <Composer onVoice={voiceEnabled ? openVoice : undefined} centered={centered} />
      {voiceOpen && <VoiceMode session={session} onClose={closeVoice} />}
    </>
  );
}

function ChatBody({ voiceBaseUrl, getVoiceAuthToken }: Omit<VoiceComposerProps, "centered">) {
  const isEmpty = useIsEmptyChat();
  const { goal } = useGoalState();
  const voiceProps = { voiceBaseUrl, getVoiceAuthToken };

  if (isEmpty) {
    return (
      <ChatIntro>
        <ComposerWithVoice centered {...voiceProps} />
      </ChatIntro>
    );
  }
  return (
    <>
      <MessageList />
      <RunningTasksStrip />
      {goal && <GoalProgressStrip goal={goal} />}
      <KeepWebAnswer />
      <Box
        sx={{
          position: "relative",
          flexShrink: 0,
          bgcolor: "var(--color-canvas)",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: -24,
            height: 24,
            pointerEvents: "none",
            background: "linear-gradient(180deg, transparent, var(--color-canvas))",
          },
        }}
      >
        <ComposerWithVoice {...voiceProps} />
      </Box>
    </>
  );
}

function ProposalsBannerController() {
  const { proposals, chat } = useChatContext();

  const prevStatusRef = useRef<TurnStatus>(TurnStatus.Idle);
  useEffect(() => {
    const current = chat.turn.status;
    if (
      prevStatusRef.current !== TurnStatus.Idle &&
      prevStatusRef.current !== TurnStatus.Complete &&
      current === TurnStatus.Complete
    ) {
      proposals.refreshAfterTurn();
    }
    prevStatusRef.current = current;
  }, [chat.turn.status, proposals]);

  return (
    <ProposalsBanner
      proposals={proposals.proposals}
      loading={proposals.loading}
      onAccept={proposals.accept}
      onReject={proposals.reject}
    />
  );
}

/**
 * The chat content column. Assumes an ambient ChatProvider (supplied by
 * WorkspaceLayout in the app, or by ChatPanel in tests). Keeps the persistent
 * provider in sync with deep-link URL params: ?session selects a chat and
 * ?project_id scopes a new chat. The URL is authoritative — sets
 * unconditionally, including to null, so a bare /workspace (New Chat, or
 * browser Forward past a ?session= entry) clears any previously-selected
 * chat instead of leaving it stale.
 */
export function ChatSurface({
  voiceBaseUrl,
  getVoiceAuthToken,
}: Omit<VoiceComposerProps, "centered"> = {}) {
  const [params] = useSearchParams();
  const { setSessionId, setProjectId } = useChatContext();
  const sessionParam = params.get("session");
  const projectParam = params.get("project_id");

  // The URL is authoritative for the persistent provider. Setting
  // unconditionally (not only when a param is present) means navigating to a
  // bare /workspace — New Chat, or browser Forward past a ?session= entry —
  // clears the previously-selected chat instead of leaving a stale conversation
  // mounted. In-app sidebar selection on /workspace calls setSessionId directly
  // without changing the URL, so sessionParam is unchanged and this effect does
  // not re-fire and cannot clobber the selection.
  useEffect(() => {
    setSessionId(sessionParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionParam]);

  useEffect(() => {
    setProjectId(projectParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectParam]);

  return (
    <>
      <CommandMenu />
      <ProjectContextStripHeader />
      <ProposalsBannerController />
      <MeetingKbBanner />
      <ChatBody voiceBaseUrl={voiceBaseUrl} getVoiceAuthToken={getVoiceAuthToken} />
    </>
  );
}
